import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, Clock, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

/**
 * ZatcaCsidBanner — iter405.
 *
 * Surfaces a "CSID expires in X days" warning on KSA banking / tax pages
 * so users never silently lose Phase 2 compliance. Pulls from the
 * existing `GET /api/settings/zatca/status` endpoint which already
 * returns:
 *   {
 *     enabled: bool,
 *     environment: 'sandbox'|'production',
 *     expiry_status: {
 *       expiry_date, days_until_expiry, is_expired, needs_renewal,
 *       alert_days
 *     }
 *   }
 *
 * Render rules:
 *   - Phase 2 disabled / no expiry set → null (no banner)
 *   - Valid + > alert_days → null (silent, healthy)
 *   - Within alert_days → amber warning
 *   - Expired → red error + "Renew now" CTA
 *
 * Dismissal: localStorage-scoped per (expiry_date, status). Dismissals
 * auto-expire when the underlying data changes — so the banner reappears
 * after a renewal that pushes the expiry date out.
 */
export function ZatcaCsidBanner({ testidPrefix = 'csid-banner' }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/zatca/status');
        setStatus(res.data || null);
      } catch { setStatus(null); }
    })();
  }, []);

  const expiry = status?.expiry_status;
  const enabled = !!status?.enabled;
  const dismissKey = expiry ? `csid-banner-dismissed::${expiry.expiry_date}::${expiry.is_expired ? 'exp' : 'warn'}` : null;

  useEffect(() => {
    if (!dismissKey) { setDismissed(false); return; }
    try { setDismissed(!!localStorage.getItem(dismissKey)); } catch { setDismissed(false); }
  }, [dismissKey]);

  // Healthy → render nothing
  if (!enabled || !expiry) return null;
  if (!expiry.is_expired && !expiry.needs_renewal) return null;
  if (dismissed && !expiry.is_expired) return null;  // hard error stays visible

  const tone = expiry.is_expired ? 'error' : 'warning';
  const Icon = expiry.is_expired ? AlertTriangle : Clock;

  const handleDismiss = () => {
    if (!dismissKey || expiry.is_expired) return;  // can't dismiss expired
    try { localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <Card
      className={tone === 'error'
        ? 'border-error/40 bg-error/5'
        : 'border-warning/40 bg-warning/5'}
      data-testid={`${testidPrefix}-${tone}`}
    >
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-full shrink-0 ${tone === 'error' ? 'bg-error/15' : 'bg-warning/15'}`}>
          <Icon size={18} className={tone === 'error' ? 'text-error' : 'text-warning'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold ${tone === 'error' ? 'text-error' : 'text-warning'}`}>
            {expiry.is_expired
              ? 'ZATCA CSID has expired — Phase 2 e-invoicing is offline'
              : `ZATCA CSID expires in ${expiry.days_until_expiry} day${expiry.days_until_expiry === 1 ? '' : 's'}`}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Expiry: {expiry.expiry_date} · Environment: {status?.environment || 'sandbox'}
            {!expiry.is_expired && expiry.alert_days ? ` · Alerts active (${expiry.alert_days}d window)` : null}
          </div>
        </div>
        <a
          href="https://fatoora.zatca.gov.sa/onboard"
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-colors ${tone === 'error' ? 'bg-error hover:bg-error/90' : 'bg-warning hover:bg-warning/90'}`}
          data-testid={`${testidPrefix}-renew`}
        >
          <Shield size={12} />Renew on Fatoora <ExternalLink size={10} />
        </a>
        <Button asChild variant="ghost" size="sm" className="text-xs" data-testid={`${testidPrefix}-config`}>
          <Link to="/zatca-phase2">Configure</Link>
        </Button>
        {!expiry.is_expired && (
          <button
            type="button"
            onClick={handleDismiss}
            className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
            title="Dismiss until renewal"
            data-testid={`${testidPrefix}-dismiss`}
          >
            <X size={13} />
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export default ZatcaCsidBanner;
